import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { Typography } from "@mui/material";

/** ===================== Helpers (contain fit + mapping + VOC XML) ===================== **/

function computeContainFit(Cw, Ch, Iw, Ih) {
  if (!Cw || !Ch || !Iw || !Ih) {
    return { scale: 1, drawnW: 0, drawnH: 0, offsetLeft: 0, offsetTop: 0 };
  }
  const scale = Math.min(Cw / Iw, Ch / Ih);
  const drawnW = Iw * scale;
  const drawnH = Ih * scale;
  const offsetLeft = (Cw - drawnW) / 2;
  const offsetTop = (Ch - drawnH) / 2;
  return { scale, drawnW, drawnH, offsetLeft, offsetTop };
}

function mapBoxToContainer(box, fit) {
  const { xmin, ymin, xmax, ymax } = box;
  const { scale, offsetLeft, offsetTop } = fit;
  return {
    left: offsetLeft + xmin * scale,
    top: offsetTop + ymin * scale,
    width: (xmax - xmin) * scale,
    height: (ymax - ymin) * scale,
  };
}

function toPercentStyles(rectPx, containerW, containerH) {
  return {
    left: (rectPx.left / containerW) * 100 + "%",
    top: (rectPx.top / containerH) * 100 + "%",
    width: (rectPx.width / containerW) * 100 + "%",
    height: (rectPx.height / containerH) * 100 + "%",
  };
}

async function parseVocXmlFromUrl(url) {
  const res = await fetch(url);

  // If S3 says 404/403/etc, just return "no boxes"
  if (!res.ok) {
    return { width: NaN, height: NaN, boxes: [] };
  }

  const text = await res.text();
  const xml = new DOMParser().parseFromString(text, "application/xml");
  if (xml.getElementsByTagName("parsererror").length) {
    // malformed XML -> treat as empty
    return { width: NaN, height: NaN, boxes: [] };
  }

  const sizeEl = xml.getElementsByTagName("size")[0];
  const width = Number(sizeEl?.getElementsByTagName("width")[0]?.textContent ?? NaN);
  const height = Number(sizeEl?.getElementsByTagName("height")[0]?.textContent ?? NaN);

  const objects = Array.from(xml.getElementsByTagName("object"));
  const boxes = [];
  for (const obj of objects) {
    const name = obj.getElementsByTagName("name")[0]?.textContent ?? "";
    const bnd = obj.getElementsByTagName("bndbox")[0];
    if (!bnd) continue;
    const xmin = Number(bnd.getElementsByTagName("xmin")[0]?.textContent ?? NaN);
    const ymin = Number(bnd.getElementsByTagName("ymin")[0]?.textContent ?? NaN);
    const xmax = Number(bnd.getElementsByTagName("xmax")[0]?.textContent ?? NaN);
    const ymax = Number(bnd.getElementsByTagName("ymax")[0]?.textContent ?? NaN);
    if ([xmin, ymin, xmax, ymax].every(Number.isFinite)) {
      boxes.push({ xmin, ymin, xmax, ymax, name });
    }
  }
  return { width, height, boxes };
}

function preloadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) return reject(new Error("no-url"));
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

/** Clamp pan to the drawn (letterboxed) image area */
function clampPanToDrawn(p, zoom, containerW, containerH, drawnW, drawnH) {
  if (zoom <= 1) return { x: 0, y: 0 };
  const zoomedW = drawnW * zoom;
  const zoomedH = drawnH * zoom;
  const maxX = Math.max(0, (zoomedW - containerW) / 2);
  const maxY = Math.max(0, (zoomedH - containerH) / 2);
  return {
    x: Math.max(-maxX, Math.min(maxX, p.x)),
    y: Math.max(-maxY, Math.min(maxY, p.y)),
  };
}

/** ===================== Component ===================== **/

/**
 * Props:
 * - mode: "upload" | "archive"
 * - organization: string
 * - getPresignedUrl(organization, key): Promise<{url:string}>
 * - fileList: string[]  (mode="upload": includes images/xml; we filter images)
 * - filePairs: {inFile:string,outFile:string,displayName?:string}[] (mode="archive")
 * - uploadDatePrefix: "YYYY/MM/DD"
 * - containerWidth, containerHeight
 * - showZoomControls?: boolean
 * - enableWheelZoom?: boolean
 * - wheelZoomMode?: "ctrlKey" | "always" | "disabled" (default "ctrlKey")
 * - zoomStep?: number
 * - minZoom?: number
 * - maxZoom?: number
 */
const LoadImagePreview = ({
  mode = "upload",
  organization,
  getPresignedUrl,
  fileList = [],
  filePairs = [],
  uploadDatePrefix = "",
  containerWidth = 400,
  containerHeight = 300,
  showZoomControls = false,
  enableWheelZoom = true,
  wheelZoomMode = "ctrlKey",
  zoomStep = 0.2,
  minZoom = 1,
  maxZoom = 3,
  captionMaxLines = 2,
  captionLineHeight = 16,
}) => {
  const [idx, setIdx] = useState(0);

  const [gtImageUrl, setGtImageUrl] = useState("");
  const [outImageUrl, setOutImageUrl] = useState("");
  const [gtXmlUrl, setGtXmlUrl] = useState("NA");

  const [gtXmlBoxes, setGtXmlBoxes] = useState([]);
  const [natural, setNatural] = useState({ w: 1, h: 1 });

  const [gtImageReady, setGtImageReady] = useState(false);
  const [gtLoading, setGtLoading] = useState(false);
  const [outLoading, setOutLoading] = useState(false);
  const [gtMissing, setGtMissing] = useState(false);
  const [outMissing, setOutMissing] = useState(false);

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const pointerStartRef = useRef({ x: 0, y: 0 });

  const fetchSeqRef = useRef(0);

  const isImage = (n) => /\.(jpe?g|png|bmp|gif|webp)$/i.test(n);

  const items =
    mode === "upload"
      ? fileList.filter(isImage).map((name) => ({ name }))
      : filePairs.map((p) => ({
          inFile: p.inFile,
          outFile: p.outFile,
          displayName: p.displayName,
        }));

  const total = items.length;
  const current = items[idx] || null;

  const buildKeys = () => {
    // Always return this shape
    const empty = {
      inKey: null,
      outKey: null,
      inXmlKey: null,
      outXmlKey: null,
      fileName: "",
    };

    if (!current) return empty;

    if (mode === "upload") {
      if (!uploadDatePrefix || !current.name) return empty;

      const fileName = current.name;
      const inKey = `in/${uploadDatePrefix}/${fileName}`;
      const outKey = `out/${uploadDatePrefix}/InferredImg_${organization}_${fileName}`;

      // XML keys (input XML mirrors input image; output XML mirrors output image base)
      const inXmlKey = `in/${uploadDatePrefix}/${fileName.replace(
        /\.[^.]+$/,
        ".xml"
      )}`;
      const outXmlKey = `out/${uploadDatePrefix}/InferredImg_${organization}_${fileName.replace(
        /\.[^.]+$/,
        ".xml"
      )}`;

      return { inKey, outKey, inXmlKey, outXmlKey, fileName };
    }

    // mode === "archive": trust keys from the API; fall back to .xml derivation only if needed
    const inKey = current.inFile || null;
    const outKey = current.outFile || null;

    const inXmlKey =
      current.inXml || (inKey ? inKey.replace(/\.[^.]+$/, ".xml") : null);

    const outXmlKey =
      current.outXml || (outKey ? outKey.replace(/\.[^.]+$/, ".xml") : null);

    // Prefer the input name for display; otherwise use the output name; otherwise empty
    const fileName =
      (inKey ? inKey.replace(/^in\//, "") : "") ||
      (outKey ? outKey.replace(/^out\//, "") : "") ||
      "";

    return { inKey, outKey, inXmlKey, outXmlKey, fileName };
  };

  // reset pan & zoom when changing image
  useEffect(() => {
    setPan({ x: 0, y: 0 });
    setZoom(1);
  }, [idx]);

  useEffect(() => {
    let cancelled = false;
    const seq = ++fetchSeqRef.current;

    (async () => {
      setGtImageReady(false);
      setGtXmlBoxes([]);
      setGtXmlUrl("NA");
      setNatural({ w: 1, h: 1 });

      setGtLoading(true);
      setOutLoading(true);
      setGtMissing(false);
      setOutMissing(false);

      if (!current) {
        setGtLoading(false);
        setOutLoading(false);
        setGtMissing(true);
        setOutMissing(true);
        return;
      }

      const { inKey, outKey, inXmlKey, outXmlKey } = buildKeys();
      if (!inKey && !outKey) {
        setGtLoading(false);
        setOutLoading(false);
        setGtMissing(true);
        setOutMissing(true);
        return;
      }

      try {
        const [inImgResp, outImgResp] = await Promise.all([
          inKey
            ? getPresignedUrl(organization, inKey).catch(() => ({ url: "" }))
            : { url: "" },
          outKey
            ? getPresignedUrl(organization, outKey).catch(() => ({ url: "" }))
            : { url: "" },
        ]);
        if (cancelled || seq !== fetchSeqRef.current) return;

        const nextGtUrl = inImgResp?.url || "";
        const nextOutUrl = outImgResp?.url || "";

        if (nextGtUrl) {
          try {
            await preloadImage(nextGtUrl);
            if (cancelled || seq !== fetchSeqRef.current) return;
            setGtImageUrl(nextGtUrl);
            setGtMissing(false);
          } catch {
            if (cancelled || seq !== fetchSeqRef.current) return;
            setGtMissing(true);
          }
        } else setGtMissing(true);
        setGtLoading(false);

        if (nextOutUrl) {
          try {
            await preloadImage(nextOutUrl);
            if (cancelled || seq !== fetchSeqRef.current) return;
            setOutImageUrl(nextOutUrl);
            setOutMissing(false);
          } catch {
            if (cancelled || seq !== fetchSeqRef.current) return;
            setOutMissing(true);
          }
        } else setOutMissing(true);
        setOutLoading(false);

        let xmlUrl = "NA";
        try {
          if (inXmlKey) {
            const inXmlResp = await getPresignedUrl(organization, inXmlKey);
            xmlUrl = inXmlResp?.url || "NA";
          }
        } catch {
          try {
            if (outXmlKey) {
              const outXmlResp = await getPresignedUrl(organization, outXmlKey);
              xmlUrl = outXmlResp?.url || "NA";
            }
          } catch {
            xmlUrl = "NA";
          }
        }
        if (cancelled || seq !== fetchSeqRef.current) return;
        setGtXmlUrl(xmlUrl);

        if (xmlUrl && xmlUrl.startsWith("http")) {
          const parsed = await parseVocXmlFromUrl(xmlUrl);
          if (cancelled || seq !== fetchSeqRef.current) return;
          setGtXmlBoxes(parsed.boxes || []);
          if (
            Number.isFinite(parsed.width) &&
            Number.isFinite(parsed.height) &&
            parsed.width > 0 &&
            parsed.height > 0
          ) {
            setNatural({ w: parsed.width, h: parsed.height });
          }
        }
      } catch {
        if (!cancelled && seq === fetchSeqRef.current) {
          setGtLoading(false);
          setOutLoading(false);
          setGtMissing(true);
          setOutMissing(true);
          setGtXmlBoxes([]);
          setGtXmlUrl("NA");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, mode, organization, uploadDatePrefix, JSON.stringify(current)]);

  const fit = computeContainFit(
    containerWidth,
    containerHeight,
    natural.w,
    natural.h
  );
  const pctBoxes = gtXmlBoxes.map((b) =>
    toPercentStyles(mapBoxToContainer(b, fit), containerWidth, containerHeight)
  );

  // Anti-drag / anti-select style
  const noDragStyle = { userSelect: "none", WebkitUserDrag: "none" };

  const captionHeight = captionMaxLines * captionLineHeight;
  const captionStyle = {
    height: captionHeight,
    marginTop: 10,
    textAlign: "center",
    fontSize: 12,
    lineHeight: `${captionLineHeight}px`,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: captionMaxLines,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    whiteSpace: captionMaxLines === 1 ? "nowrap" : "normal",
  };

  // Pan (mouse only; don’t hijack touch/trackpad scroll)
  const onPointerDown = (e) => {
    // only left mouse button + primary pointer starts pan
    if (!(e.isPrimary && e.pointerType === "mouse" && e.button === 0)) return;
    e.preventDefault(); // cancel img drag/select
    if (zoom <= 1) return;
    setIsPanning(true);
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = { x: pan.x, y: pan.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isPanning) return;
    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    setPan(() =>
      clampPanToDrawn(
        { x: panStartRef.current.x + dx, y: panStartRef.current.y + dy },
        zoom,
        containerWidth,
        containerHeight,
        fit.drawnW,
        fit.drawnH
      )
    );
  };

  const onPointerUp = (e) => {
    if (!isPanning) return;
    setIsPanning(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Wheel → zoom only with modifier (Ctrl/⌘) unless configured otherwise
  const onWheel = (e) => {
    if (!enableWheelZoom || wheelZoomMode === "disabled") return;

    const wantsZoom =
      wheelZoomMode === "always" ||
      (wheelZoomMode === "ctrlKey" && (e.ctrlKey || e.metaKey));

    if (!wantsZoom) return; // let page scroll normally

    e.preventDefault(); // consume only when we will zoom
    const direction = e.deltaY > 0 ? -1 : 1;
    const nextZoom = Math.min(
      maxZoom,
      Math.max(minZoom, +(zoom + direction * zoomStep).toFixed(2))
    );
    setZoom(nextZoom);
    setPan((p) =>
      nextZoom === 1
        ? { x: 0, y: 0 }
        : clampPanToDrawn(p, nextZoom, containerWidth, containerHeight, fit.drawnW, fit.drawnH)
    );
  };

  const totalItems = total;
  const currentDisplay = Math.min(idx + 1, totalItems || 1);

  // Caption helpers

  const getGtCaption = () => {
    if (!current) return "N/A";

    // Upload mode: Ground Truth = original uploaded filename
    if (mode === "upload") {
      return current.name || "N/A";
    }

    // Archive/streaming mode:
    // in/2025/12/05/gun1.jpeg -> 2025/12/05/gun1.jpeg
    const raw = current.inFile || "";
    return raw.replace(/^in\//, "") || "N/A";
  };

  const getInfCaption = () => {
    if (!current) return "N/A";

    // Upload mode: show InferredImg_<org>_<file> (as before)
    if (mode === "upload") {
      return (
        (current.name && `InferredImg_${organization}_${current.name}`) || "N/A"
      );
    }

    // Archive/streaming mode:
    // out/2025/12/05/InferredImg_org_gun1.jpeg
    //   -> 2025/12/05/InferredImg_org_gun1.jpeg
    const raw = current.outFile || "";
    return raw.replace(/^out\//, "") || "N/A";
  };

  const zoomIn = () =>
    setZoom((z) => {
      const nz = Math.min(maxZoom, +(z + zoomStep).toFixed(2));
      setPan((p) =>
        clampPanToDrawn(p, nz, containerWidth, containerHeight, fit.drawnW, fit.drawnH)
      );
      return nz;
    });

  const zoomOut = () =>
    setZoom((z) => {
      const nz = Math.max(minZoom, +(z - zoomStep).toFixed(2));
      setPan((p) =>
        nz === 1
          ? { x: 0, y: 0 }
          : clampPanToDrawn(p, nz, containerWidth, containerHeight, fit.drawnW, fit.drawnH)
      );
      return nz;
    });

  const Pane = ({ children, loading, missing }) => (
    <div
      style={{
        position: "relative",
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        overflow: "hidden",
        border: "1px solid #ccc",
        background: "#f9f9f9",
        cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
        // allow page scroll normally; only when panning we block native gestures
        touchAction: isPanning ? "none" : "auto",
        ...noDragStyle,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
      onWheel={onWheel}
      onDragStart={(e) => e.preventDefault()}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
          transformOrigin: "center center",
        }}
      >
        {children}

        {loading && !missing && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(1px)",
            }}
          />
        )}

        {!loading && missing && (
          <Typography
            variant="body2"
            sx={{ color: "gray", textAlign: "center", mt: 1 }}
          >
            Image not available
          </Typography>
        )}
      </div>
    </div>
  );

  return (
    <>
      {totalItems > 0 && (
        <Row style={{ marginTop: "50px" }}>
          <Col sm="12">
            <Typography
              variant="body2"
              sx={{ textAlign: "center", color: "text.secondary" }}
            >
              Showing image {currentDisplay}/{totalItems}
            </Typography>
          </Col>
        </Row>
      )}

      <Row style={{ marginTop: "10px" }}>
        <Col sm="1"></Col>
        <Col sm="5">
          <p
            style={{
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            Ground Truth
          </p>
        </Col>
        <Col sm="5">
          <p
            style={{
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            Inference Result
          </p>
        </Col>
        <Col sm="1"></Col>
      </Row>

      {/* Previews + nav */}
      <Row style={{ marginTop: "24px" }}>
        <Col sm="1">
          <Button
            size="sm"
            disabled={idx <= 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
          >
            {"<<"}
          </Button>
        </Col>

        {/* Ground Truth */}
        <Col sm="5">
          <Pane loading={gtLoading} missing={gtMissing && !gtImageUrl}>
            {gtImageUrl ? (
              <img
                src={gtImageUrl}
                alt="Ground Truth"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  opacity: gtLoading ? 0.6 : 1,
                  transition: "opacity 120ms linear",
                  ...noDragStyle,
                }}
                onLoad={(e) => {
                  if (!(natural.w > 1 && natural.h > 1)) {
                    const { naturalWidth, naturalHeight } = e.currentTarget;
                    if (naturalWidth && naturalHeight)
                      setNatural({ w: naturalWidth, h: naturalHeight });
                  }
                  setGtImageReady(true);
                }}
              />
            ) : null}

            {gtImageReady &&
              pctBoxes.length > 0 &&
              pctBoxes.map((rect, i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    ...rect,
                    border: "2px solid blue",
                    background: "rgba(55, 6, 251, 0.25)",
                    pointerEvents: "none",
                  }}
                />
              ))}
          </Pane>

          <div style={captionStyle} title={getGtCaption()}>
            <strong>File Name:</strong>{" "}
            {getGtCaption()}
          </div>
        </Col>

        {/* Inference result */}
        <Col sm="5">
          <Pane loading={outLoading} missing={outMissing && !outImageUrl}>
            {outImageUrl ? (
              <img
                src={outImageUrl}
                alt="Inference Result"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  opacity: outLoading ? 0.6 : 1,
                  transition: "opacity 120ms linear",
                  ...noDragStyle,
                }}
              />
            ) : null}
          </Pane>

          <div style={captionStyle} title={getInfCaption()}>
            <strong>File Name:</strong>{" "}
            {getInfCaption()}
          </div>
        </Col>

        <Col sm="1">
          <Button
            size="sm"
            disabled={idx >= totalItems - 1}
            onClick={() =>
              setIdx((i) => Math.min(totalItems - 1, i + 1))
            }
          >
            {">>"}
          </Button>
        </Col>
      </Row>

      {/* Optional zoom controls */}
      {showZoomControls && (
        <Row style={{ marginTop: 12 }}>
          <Col sm="12" style={{ textAlign: "center" }}>
            <Button size="sm" onClick={zoomIn}>
              +
            </Button>
            <Button size="sm" onClick={zoomOut} style={{ marginLeft: 10 }}>
              -
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default LoadImagePreview;
